import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/adminService';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

const UserManagementAnalytics = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userActivity, setUserActivity] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);

  // Check admin access
  const isAdmin = (user?.email === 'demo@rovynbudget.com' || user?.email === 'rovyngroupinc@gmail.com') && userProfile?.role === 'admin';

  useEffect(() => {
    if (userProfile && !isAdmin) {
      navigate('/dashboard');
    }
  }, [userProfile, isAdmin, navigate]);

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin, roleFilter, sortBy]);

  useEffect(() => {
    applyFilters();
  }, [users, searchQuery]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await adminService?.getAllUsers({
        role: roleFilter,
        sortBy: sortBy
      });
      setUsers(data);
      setFilteredUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...users];

    if (searchQuery) {
      filtered = filtered?.filter(
        (u) =>
          u?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
          u?.fullName?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    setFilteredUsers(filtered);
  };

  const loadUserActivity = async (userId) => {
    try {
      const activity = await adminService?.getUserActivity(userId);
      setUserActivity(activity);
    } catch (error) {
      console.error('Error loading user activity:', error);
    }
  };

  const handleUserClick = async (user) => {
    setSelectedUser(user);
    setShowUserDetails(true);
    await loadUserActivity(user?.id);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityStatus = (lastActivityDate) => {
    if (!lastActivityDate) return { label: 'Inactive', color: 'text-muted-foreground' };
    
    const daysSinceActivity = Math.floor(
      (new Date() - new Date(lastActivityDate)) / (1000 * 60 * 60 * 24)
    );

    if (daysSinceActivity <= 7) return { label: 'Active', color: 'text-success' };
    if (daysSinceActivity <= 30) return { label: 'Moderate', color: 'text-warning' };
    return { label: 'Inactive', color: 'text-error' };
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 px-16 md:px-24 lg:px-32 pb-32">
        <div className="max-w-[1600px] mx-auto">
          {/* Page Header */}
          <div className="mb-32 mt-24">
            <div className="flex items-center gap-12 mb-12">
              <Icon name="Users" size={32} color="var(--color-primary)" strokeWidth={2.5} />
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-secondary">
                User Management & Analytics
              </h1>
            </div>
            <p className="text-base text-muted-foreground font-caption">
              Comprehensive user oversight with detailed activity tracking and behavioral analytics
            </p>
          </div>

          {/* Filters and Search */}
          <div className="bg-card rounded-lg p-24 shadow-elevation-2 mb-24">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
              <div>
                <label className="block text-sm font-caption text-muted-foreground mb-8">
                  Search Users
                </label>
                <div className="relative">
                  <Icon
                    name="Search"
                    size={18}
                    color="var(--color-muted-foreground)"
                    className="absolute left-12 top-1/2 transform -translate-y-1/2"
                  />
                  <Input
                    type="text"
                    placeholder="Search by email or name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="pl-40"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-caption text-muted-foreground mb-8">
                  Filter by Role
                </label>
                <Select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e?.target?.value)}
                >
                  <option value="all">All Roles</option>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-caption text-muted-foreground mb-8">
                  Sort By
                </label>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e?.target?.value)}
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="email">Email (A-Z)</option>
                </Select>
              </div>
            </div>

            <div className="mt-16 flex items-center gap-12">
              <Icon name="Users" size={16} color="var(--color-primary)" />
              <span className="text-sm font-caption text-foreground">
                <span className="font-semibold">{filteredUsers?.length}</span> users found
              </span>
            </div>
          </div>

          {/* User Table */}
          {loading ? (
            <div className="flex items-center justify-center py-64">
              <Icon name="Loader" size={32} color="var(--color-primary)" className="animate-spin" />
            </div>
          ) : (
            <div className="bg-card rounded-lg shadow-elevation-2 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left py-16 px-16 text-sm font-caption font-semibold text-muted-foreground">
                        User
                      </th>
                      <th className="text-left py-16 px-16 text-sm font-caption font-semibold text-muted-foreground">
                        Email
                      </th>
                      <th className="text-left py-16 px-16 text-sm font-caption font-semibold text-muted-foreground">
                        Role
                      </th>
                      <th className="text-left py-16 px-16 text-sm font-caption font-semibold text-muted-foreground">
                        Tier
                      </th>
                      <th className="text-left py-16 px-16 text-sm font-caption font-semibold text-muted-foreground">
                        Points
                      </th>
                      <th className="text-left py-16 px-16 text-sm font-caption font-semibold text-muted-foreground">
                        Status
                      </th>
                      <th className="text-left py-16 px-16 text-sm font-caption font-semibold text-muted-foreground">
                        Registered
                      </th>
                      <th className="text-right py-16 px-16 text-sm font-caption font-semibold text-muted-foreground">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers?.length > 0 ? (
                      filteredUsers?.map((u) => {
                        const activityStatus = getActivityStatus(u?.lastActivityDate);
                        return (
                          <tr
                            key={u?.id}
                            className="border-b border-border hover:bg-muted/50 transition-smooth cursor-pointer"
                            onClick={() => handleUserClick(u)}
                          >
                            <td className="py-16 px-16">
                              <div className="flex items-center gap-12">
                                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                                  <Icon name="User" size={16} color="var(--color-primary)" />
                                </div>
                                <p className="text-sm font-caption font-medium text-foreground">
                                  {u?.fullName || 'Unknown User'}
                                </p>
                              </div>
                            </td>
                            <td className="py-16 px-16">
                              <p className="text-sm font-caption text-foreground">{u?.email}</p>
                            </td>
                            <td className="py-16 px-16">
                              <span
                                className={`inline-block px-8 py-4 rounded text-xs font-caption ${
                                  u?.role === 'admin' ?'bg-primary/10 text-primary' :'bg-muted text-foreground'
                                }`}
                              >
                                {u?.role}
                              </span>
                            </td>
                            <td className="py-16 px-16">
                              <span className="inline-block px-8 py-4 bg-warning/10 text-warning rounded text-xs font-caption">
                                {u?.tier}
                              </span>
                            </td>
                            <td className="py-16 px-16">
                              <p className="text-sm font-caption font-medium text-foreground">
                                {u?.totalPoints?.toLocaleString()}
                              </p>
                            </td>
                            <td className="py-16 px-16">
                              <span className={`text-sm font-caption font-medium ${activityStatus?.color}`}>
                                {activityStatus?.label}
                              </span>
                            </td>
                            <td className="py-16 px-16">
                              <p className="text-sm font-caption text-muted-foreground">
                                {formatDate(u?.createdAt)}
                              </p>
                            </td>
                            <td className="py-16 px-16 text-right">
                              <button
                                onClick={(e) => {
                                  e?.stopPropagation();
                                  handleUserClick(u);
                                }}
                                className="px-12 py-6 bg-primary/10 text-primary text-xs font-caption rounded hover:bg-primary/20 transition-smooth"
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="8" className="py-32 text-center">
                          <Icon
                            name="Users"
                            size={48}
                            color="var(--color-muted-foreground)"
                            className="mx-auto mb-12 opacity-50"
                          />
                          <p className="text-base text-muted-foreground font-caption">
                            No users found matching your filters
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>
      {/* User Details Modal */}
      {showUserDetails && selectedUser && (
        <div
          className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-16"
          onClick={() => setShowUserDetails(false)}
        >
          <div
            className="bg-card rounded-lg shadow-elevation-4 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="p-24 border-b border-border">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-16">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="User" size={24} color="var(--color-primary)" />
                  </div>
                  <div>
                    <h2 className="text-xl font-heading font-bold text-foreground">
                      {selectedUser?.fullName || 'Unknown User'}
                    </h2>
                    <p className="text-sm text-muted-foreground font-caption">
                      {selectedUser?.email}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowUserDetails(false)}
                  className="p-8 hover:bg-muted rounded-lg transition-smooth"
                >
                  <Icon name="X" size={20} color="var(--color-foreground)" />
                </button>
              </div>
            </div>

            <div className="p-24">
              {/* User Info */}
              <div className="mb-24">
                <h3 className="text-base font-heading font-semibold text-foreground mb-16">
                  Account Information
                </h3>
                <div className="grid grid-cols-2 gap-16">
                  <div>
                    <p className="text-sm text-muted-foreground font-caption mb-4">Role</p>
                    <span
                      className={`inline-block px-12 py-6 rounded text-sm font-caption ${
                        selectedUser?.role === 'admin' ?'bg-primary/10 text-primary' :'bg-muted text-foreground'
                      }`}
                    >
                      {selectedUser?.role}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground font-caption mb-4">Tier</p>
                    <span className="inline-block px-12 py-6 bg-warning/10 text-warning rounded text-sm font-caption">
                      {selectedUser?.tier}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground font-caption mb-4">Total Points</p>
                    <p className="text-base font-caption font-semibold text-foreground">
                      {selectedUser?.totalPoints?.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground font-caption mb-4">Registered</p>
                    <p className="text-base font-caption text-foreground">
                      {formatDate(selectedUser?.createdAt)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Activity Stats */}
              {userActivity && (
                <div>
                  <h3 className="text-base font-heading font-semibold text-foreground mb-16">
                    Activity Overview
                  </h3>
                  <div className="grid grid-cols-2 gap-16">
                    <div className="p-16 bg-muted rounded-lg">
                      <div className="flex items-center gap-8 mb-8">
                        <Icon name="CreditCard" size={16} color="var(--color-primary)" />
                        <p className="text-sm text-muted-foreground font-caption">Transactions</p>
                      </div>
                      <p className="text-2xl font-heading font-bold text-foreground">
                        {userActivity?.transactionCount}
                      </p>
                    </div>
                    <div className="p-16 bg-muted rounded-lg">
                      <div className="flex items-center gap-8 mb-8">
                        <Icon name="Target" size={16} color="var(--color-success)" />
                        <p className="text-sm text-muted-foreground font-caption">Budgets</p>
                      </div>
                      <p className="text-2xl font-heading font-bold text-foreground">
                        {userActivity?.budgetCount}
                      </p>
                    </div>
                    <div className="p-16 bg-muted rounded-lg">
                      <div className="flex items-center gap-8 mb-8">
                        <Icon name="TrendingUp" size={16} color="var(--color-accent)" />
                        <p className="text-sm text-muted-foreground font-caption">Goals</p>
                      </div>
                      <p className="text-2xl font-heading font-bold text-foreground">
                        {userActivity?.goalCount}
                      </p>
                    </div>
                    <div className="p-16 bg-muted rounded-lg">
                      <div className="flex items-center gap-8 mb-8">
                        <Icon name="BookOpen" size={16} color="var(--color-warning)" />
                        <p className="text-sm text-muted-foreground font-caption">Blog Engagement</p>
                      </div>
                      <p className="text-2xl font-heading font-bold text-foreground">
                        {userActivity?.blogEngagement}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementAnalytics;